import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { IList, tuple } from 'annotation-tools/utils';
import React, { FC, memo, MouseEvent, useMemo } from 'react';
import styled from 'styled-components';
import { colorDef } from '../../../constants/styles/color-def';
import { viewTexts } from '../../../constants/view-texts';
import { projectStatusViewValue } from '../../../functions/convert-to-view-value/project-status-view-value';
import { ProjectStatus } from '../../../types/enum/project-status';
import { IProjectType } from '../../../types/record/project';
import { CreateNewProjectDialog } from '../../dialogs/create-new-project-dialog/create-new-project-dialog';
import { DeleteProjectDialog } from '../../dialogs/delete-project-dialog/delete-project-dialog';
import { EditProjectDialog } from '../../dialogs/edit-project-dialog/edit-project-dialog';
import { MuiButton } from '../../shared/form-element';
import { MuiPaper } from '../../shared/mui/mui-paper';
import { MuiTablePagination } from '../../shared/mui/mui-table-pagination';
import { MuiTableRow } from '../../shared/mui/mui-table-row';
import { Root } from '../../shared/styled/root';
import { ProjectSearchBar } from './project-search-bar';

const vt = viewTexts.pages.projectList;

const CreateButtonWrapper = styled.div`
  padding: 10px;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ButtonWrapper = styled.div`
  padding: 0 5px;
  display: inline-block;
`;

interface Props {
  searchString: string;
  onSearchStringChange: (value: string) => void;
  status: ProjectStatus | undefined;
  onStatusChange: (value: ProjectStatus | undefined) => void;
  author: string | undefined;
  onAuthorChange: (value: string | undefined) => void;
  approver: string | undefined;
  onApproverChange: (value: string | undefined) => void;
  page: number;
  onPageChange: (value: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: number) => void;
  numRows: number;
  authorOptions: { id: string; label: string }[];
  approverOptions: { id: string; label: string }[];
  companyId: number;
  projectListFilteredCurrentPage: IList<IProjectType>;
  projectOnClick: (id: number) => void;
  createNewProjectDialogOpen: boolean;
  createNewProjectDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  createNewProjectOnClick: () => void;
  editProjectOnClick: (project: IProjectType) => void;
  editProjectDialogIsOpen: boolean;
  editProjectDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  projectToEdit: IProjectType | undefined;
  deleteProjectOnClick: (project: IProjectType) => void;
  deleteProjectDialogIsOpen: boolean;
  deleteProjectDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  projectToDelete: IProjectType | undefined;
}

export const ProjectListView: FC<Props> = memo(
  ({
    searchString,
    onSearchStringChange,
    status,
    onStatusChange,
    author,
    onAuthorChange,
    approver,
    onApproverChange,
    page,
    onPageChange,
    rowsPerPage,
    onRowsPerPageChange,
    numRows,
    authorOptions,
    approverOptions,
    companyId,
    projectListFilteredCurrentPage,
    projectOnClick,
    createNewProjectDialogOpen,
    createNewProjectDialogOnClose,
    createNewProjectOnClick,
    editProjectOnClick,
    editProjectDialogIsOpen,
    editProjectDialogOnClose,
    projectToEdit,
    deleteProjectOnClick,
    deleteProjectDialogIsOpen,
    deleteProjectDialogOnClose,
    projectToDelete
  }: Readonly<Props>) => {
    const projectsWithHandler = useMemo(
      () =>
        projectListFilteredCurrentPage.map(project =>
          tuple(
            {
              id: project.id,
              name: project.name,
              status: projectStatusViewValue(project.status),
              author: project.author,
              approver: project.approver,
              dateCreatedString: new Date(project.dateCreated).toLocaleString(),
              dateUpdatedString: new Date(project.dateUpdated).toLocaleString(),
              canBeDeleted: project.canBeDeleted
            },
            () => projectOnClick(project.id),
            (event: MouseEvent) => {
              event.stopPropagation();
              editProjectOnClick(project);
            },
            (event: MouseEvent) => {
              event.stopPropagation();
              deleteProjectOnClick(project);
            }
          )
        ),
      [
        projectListFilteredCurrentPage,
        projectOnClick,
        editProjectOnClick,
        deleteProjectOnClick
      ]
    );

    return (
      <Root>
        <SearchBarWrapper>
          <ProjectSearchBar
            name={searchString}
            onInput={onSearchStringChange}
            status={status}
            onStatusChange={onStatusChange}
            author={author}
            onAuthorChange={onAuthorChange}
            approver={approver}
            onApproverChange={onApproverChange}
            authorOptions={authorOptions}
            approverOptions={approverOptions}
          />
          <CreateButtonWrapper>
            <MuiButton
              label={vt.buttons.create}
              onClick={createNewProjectOnClick}
            />
          </CreateButtonWrapper>
        </SearchBarWrapper>

        <MuiPaper bgColor={colorDef.paperLighter} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{vt.header.name}</TableCell>
                <TableCell>{vt.header.status}</TableCell>
                <TableCell>{vt.header.author}</TableCell>
                <TableCell>{vt.header.approver}</TableCell>
                <TableCell>{vt.header.dateCreated}</TableCell>
                <TableCell>{vt.header.dateUpdated}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {projectsWithHandler.map(
                ([
                  project,
                  rowOnClickHandler,
                  editButtonOnClickHandler,
                  deleteButtonOnClickHandler
                ]) => (
                  <MuiTableRow key={project.id} onClick={rowOnClickHandler}>
                    <TableCell>{project.name}</TableCell>
                    <TableCell>{project.status}</TableCell>
                    <TableCell>{project.author}</TableCell>
                    <TableCell>{project.approver}</TableCell>
                    <TableCell>{project.dateCreatedString}</TableCell>
                    <TableCell>{project.dateUpdatedString}</TableCell>
                    <TableCell align='right'>
                      <ButtonWrapper>
                        <MuiButton
                          label={vt.buttons.edit}
                          onClick={editButtonOnClickHandler}
                        />
                      </ButtonWrapper>
                      <ButtonWrapper
                        title={
                          project.canBeDeleted
                            ? undefined
                            : viewTexts.undeletableReason.project
                        }
                      >
                        <MuiButton
                          color='secondary'
                          label={vt.buttons.delete}
                          onClick={deleteButtonOnClickHandler}
                          disabled={!project.canBeDeleted}
                        />
                      </ButtonWrapper>
                    </TableCell>
                  </MuiTableRow>
                )
              )}
            </TableBody>
          </Table>
          <MuiTablePagination
            numRows={numRows}
            page={page}
            onChangePage={onPageChange}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        </MuiPaper>

        <CreateNewProjectDialog
          open={createNewProjectDialogOpen}
          onClose={createNewProjectDialogOnClose}
          companyId={companyId}
        />
        {projectToEdit !== undefined ? (
          <EditProjectDialog
            open={editProjectDialogIsOpen}
            onClose={editProjectDialogOnClose}
            companyId={companyId}
            project={projectToEdit}
          />
        ) : (
          undefined
        )}
        {projectToDelete !== undefined ? (
          <DeleteProjectDialog
            open={deleteProjectDialogIsOpen}
            onClose={deleteProjectDialogOnClose}
            project={projectToDelete}
            companyId={companyId}
          />
        ) : (
          undefined
        )}
      </Root>
    );
  }
);

ProjectListView.displayName = 'ProjectListView';
